import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ChevronUp, ChevronDown, ArrowUpRight, Shield, Award, Star, Trophy, Crown, Zap, Gift } from "lucide-react";

// Use relative API path so it works on any domain (custom domain or preview URL)
const API = '/api';

// GIF URLs for decorating the page
const GIFS = {
  hero_coins: "https://media2.giphy.com/media/MkvZFvzHIWbRK/200.gif",
  hero_treasure: "https://media0.giphy.com/media/dQjcx6FlY2OB5DTnCj/200.gif",
  category_trophy: "https://media4.giphy.com/media/XE8iJs1sT7xTM5lafa/giphy.gif",
  category_money: "https://media3.giphy.com/media/DRkzXuhzKEQDf0LEaw/giphy.gif",
  category_star: "https://media3.giphy.com/media/3Gm15eZOsNk0tptIuG/giphy.gif",
  coin_spin: "https://media4.giphy.com/media/RRmVIBEcGbEqlkIV2u/200.gif",
  money_grow: "https://media0.giphy.com/media/lNUVjWo9IlRQsnApcw/200.gif",
};

// Jumping star GIF for all firms
const JUMPING_STAR_GIF = "https://media1.giphy.com/media/XKMEueO6tK8U9tzXQt/giphy.gif";

// Category GIF mapping
const getCategoryGif = (category) => {
  if (category.includes("Kazandıran")) return GIFS.category_trophy;
  if (category.includes("Bonus")) return GIFS.category_money;
  if (category.includes("Tercih")) return GIFS.category_star;
  return GIFS.coin_spin;
};

// Logo component - shows image logo if available, fallback to initials
const FirmLogo = ({ initials, color, name, logoUrl }) => {
  const [imgError, setImgError] = useState(false);

  if (logoUrl && !imgError) {
    return (
      <div
        className="logo-frame h-14 w-14 rounded-xl flex items-center justify-center shadow-sm border border-[hsl(var(--border))] overflow-hidden bg-white"
        data-testid="firm-logo"
      >
        <img
          src={logoUrl}
          alt={`${name} logo`}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      </div>
    );
  }

  return (
    <div
      className="logo-frame h-14 w-14 rounded-xl flex items-center justify-center shadow-sm border border-[hsl(var(--border))]"
      style={{ backgroundColor: color }}
      data-testid="firm-logo"
    >
      <span className="text-white font-bold text-lg tracking-wider font-body" style={{ fontFamily: "'Spectral', serif" }}>
        {initials}
      </span>
    </div>
  );
};

// Category icon mapping
const getCategoryIcon = (category) => {
  if (category.includes("Kazandıran")) return <Trophy className="w-5 h-5 text-[hsl(var(--brand-gold))]" />;
  if (category.includes("Bonus")) return <Gift className="w-5 h-5 text-[hsl(var(--brand-gold))]" />;
  if (category.includes("Tercih")) return <Star className="w-5 h-5 text-[hsl(var(--brand-gold))]" />;
  return <Award className="w-5 h-5 text-[hsl(var(--brand-gold))]" />;
};

// Category subtitle mapping
const getCategorySubtitle = (category) => {
  if (category.includes("Kazandıran")) return "En yüksek kazanç oranlarına sahip, güvenilir ve lisanslı platformlar.";
  if (category.includes("Bonus")) return "Yeni üyelere özel cömert deneme bonusları sunan siteler.";
  if (category.includes("Tercih")) return "Kullanıcıların en çok tercih ettiği, kanıtlanmış kaliteli platformlar.";
  return "";
};

// Firm Card Component with GIF decoration
const FirmCard = ({ firm, index, isAdmin, onMove, isFirst, isLast, categoryIndex }) => {
  const [isMoving, setIsMoving] = useState(false);

  const handleMove = async (direction) => {
    setIsMoving(true);
    await onMove(firm.id, direction);
    setIsMoving(false);
  };

  return (
    <Card
      className="firm-card rounded-2xl border bg-white p-0 group"
      data-testid={`firm-card-${categoryIndex}-${index}`}
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-4 firm-card-inner">
          {/* Rank + Logo */}
          <div className="flex items-center gap-3 shrink-0">
            <span className="rank-number" data-testid="firm-rank">
              {index + 1}
            </span>
            <FirmLogo
              initials={firm.logo_initials}
              color={firm.logo_color}
              name={firm.name}
              logoUrl={firm.logo_url}
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3
                className="font-heading text-lg font-semibold text-[hsl(var(--foreground))]"
                style={{ fontFamily: "'Spectral', serif" }}
                data-testid="firm-card-title"
              >
                {firm.name}
              </h3>
              {/* Jumping star GIF next to firm name */}
              <img
                src={JUMPING_STAR_GIF}
                alt=""
                className="firm-gif-icon"
                loading="lazy"
              />
            </div>
            <p
              className="text-sm text-[hsl(var(--muted-foreground))] mb-3 leading-relaxed line-clamp-2"
              data-testid="firm-card-description"
            >
              {firm.description}
            </p>
            <Badge
              className="tagline-badge text-xs font-medium px-3 py-1 rounded-full"
              data-testid="firm-card-tagline"
            >
              {firm.tagline}
            </Badge>
          </div>

          {/* CTA + Admin */}
          <div className="flex flex-col items-end gap-2 shrink-0 firm-card-actions">
            <a
              href={firm.referral_url}
              target="_blank"
              rel="noopener noreferrer"
              className="no-underline"
              data-testid={`firm-${categoryIndex}-${index}-cta-button`}
            >
              <Button
                className="cta-btn rounded-xl bg-[hsl(var(--brand-ink))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--brand-ink))]/90 px-5 py-2 h-10 font-medium"
              >
                Siteye Git
                <ArrowUpRight className="w-4 h-4 ml-1 cta-icon" />
              </Button>
            </a>

            {isAdmin && (
              <div
                className="admin-controls flex gap-1"
                data-testid="admin-reorder-controls"
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-lg hover:bg-[hsl(var(--accent))]"
                  disabled={isFirst || isMoving}
                  onClick={() => handleMove("up")}
                  data-testid="admin-move-up-button"
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 rounded-lg hover:bg-[hsl(var(--accent))]"
                  disabled={isLast || isMoving}
                  onClick={() => handleMove("down")}
                  data-testid="admin-move-down-button"
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

// Category Section Component with GIF
const CategorySection = ({ categoryData, categoryIndex, isAdmin, onMove }) => {
  const { category, firms } = categoryData;
  const categoryGif = getCategoryGif(category);

  return (
    <section
      className="mb-8 sm:mb-12"
      data-testid={`category-section-${categoryIndex}`}
      id={`category-${categoryIndex}`}
    >
      {/* Category Header with GIF */}
      <div className="flex items-center gap-3 mb-2">
        {getCategoryIcon(category)}
        <h2
          className="font-heading text-xl sm:text-2xl font-semibold text-[hsl(var(--foreground))]"
          style={{ fontFamily: "'Spectral', serif" }}
          data-testid={`category-title-${categoryIndex}`}
        >
          {category}
        </h2>
        {/* Animated GIF next to category title */}
        <img
          src={categoryGif}
          alt=""
          className="category-gif"
          loading="lazy"
        />
      </div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5 ml-8">
        {getCategorySubtitle(category)}
      </p>

      {/* Firm Cards */}
      <div className="space-y-3 sm:space-y-4">
        {firms.map((firm, index) => (
          <FirmCard
            key={firm.id}
            firm={firm}
            index={index}
            categoryIndex={categoryIndex}
            isAdmin={isAdmin}
            onMove={onMove}
            isFirst={index === 0}
            isLast={index === firms.length - 1}
          />
        ))}
      </div>
    </section>
  );
};

// Header Bar
const HeaderBar = ({ isAdmin }) => (
  <header
    className="sticky top-0 z-40 border-b bg-white/70 backdrop-blur-md"
    data-testid="topbar"
  >
    <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Shield className="w-5 h-5 text-[hsl(var(--brand-gold))]" />
        <span className="text-sm font-medium text-[hsl(var(--muted-foreground))]">
          Lisanslı & Güvenilir İçerik
        </span>
      </div>
      {isAdmin && (
        <Badge
          className="bg-[hsl(var(--brand-gold-soft))] text-[hsl(var(--brand-ink))] border border-[hsl(var(--brand-gold))]/20 text-xs"
          data-testid="admin-mode-indicator"
        >
          <Zap className="w-3 h-3 mr-1" />
          Admin Modu
        </Badge>
      )}
    </div>
  </header>
);

// Hero Section with GIF decorations
const HeroSection = () => (
  <section className="hero-gradient relative overflow-hidden rounded-2xl border bg-white/60 p-6 sm:p-10 mb-8 sm:mb-12" style={{ boxShadow: 'var(--shadow-soft)' }}>
    {/* Decorative GIF - coins on right side */}
    <div className="hero-gif-container">
      <img
        src={GIFS.hero_coins}
        alt=""
        className="hero-gif hero-gif-coins"
        loading="lazy"
      />
      <img
        src={GIFS.hero_treasure}
        alt=""
        className="hero-gif hero-gif-treasure"
        loading="lazy"
      />
    </div>

    <div className="relative z-10">
      <h1
        className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[hsl(var(--foreground))] mb-3"
        style={{ fontFamily: "'Spectral', serif" }}
        data-testid="hero-title"
      >
        Güvenilir ve Lisanslı Siteler
      </h1>
      <div className="gold-line mb-4"></div>
      <p className="text-base sm:text-lg text-[hsl(var(--muted-foreground))] max-w-2xl leading-relaxed mb-5">
        2026'da öne çıkan firmalar — hızlı karşılaştır, güvenle seç.
      </p>

      {/* Trust Badges */}
      <div className="flex flex-wrap gap-3">
        <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] bg-white/80 rounded-full px-4 py-2 border border-[hsl(var(--border))]">
          <Shield className="w-4 h-4 text-[hsl(var(--brand-success))]" />
          Lisanslı Siteler
        </div>
        <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] bg-white/80 rounded-full px-4 py-2 border border-[hsl(var(--border))]">
          <Zap className="w-4 h-4 text-[hsl(var(--brand-gold))]" />
          Hızlı Ödeme
        </div>
        <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] bg-white/80 rounded-full px-4 py-2 border border-[hsl(var(--border))]">
          <Crown className="w-4 h-4 text-[hsl(var(--brand-gold))]" />
          VIP Hizmet
        </div>
      </div>
    </div>
  </section>
);

// Footer
const FooterSection = () => (
  <footer className="border-t mt-8 sm:mt-16 pt-6 pb-10 text-center">
    <div className="max-w-[1120px] mx-auto px-4 sm:px-6">
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-2">
        18+ | Sorumlu oyun | Bilgilendirme amaçlıdır
      </p>
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        © 2026 Güvenilir ve Lisanslı Siteler. Tüm hakları saklıdır.
      </p>
    </div>
  </footer>
);

// Main Landing Page
const LandingPage = () => {
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "1";
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFirms = useCallback(async () => {
    try {
      setLoading(true);
      // First seed, then fetch
      await axios.post(`${API}/firms/seed`).catch(() => {});
      const response = await axios.get(`${API}/firms/grouped`);
      setCategories(response.data.categories || []);
      setError(null);
    } catch (e) {
      console.error("Error fetching firms:", e);
      setError("Veriler yüklenirken bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFirms();
  }, [fetchFirms]);

  const handleMove = async (firmId, direction) => {
    try {
      await axios.post(`${API}/firms/${firmId}/move`, { direction });
      await fetchFirms();
    } catch (e) {
      console.error("Error moving firm:", e);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))]">
        <HeaderBar isAdmin={false} />
        <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
          <div className="space-y-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 bg-white rounded-2xl border animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-[hsl(var(--destructive))] mb-4">{error}</p>
          <Button onClick={fetchFirms}>Tekrar Dene</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="landing-page">
      {/* Noise overlay for premium texture */}
      <div className="noise-overlay" />

      <HeaderBar isAdmin={isAdmin} />

      <main className="relative z-10 max-w-[1120px] mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <HeroSection />

        {categories.map((catData, catIndex) => (
          <div key={catData.category}>
            {catIndex > 0 && (
              <Separator className="mb-8 sm:mb-12 bg-[hsl(var(--border))]" />
            )}
            <CategorySection
              categoryData={catData}
              categoryIndex={catIndex}
              isAdmin={isAdmin}
              onMove={handleMove}
            />
          </div>
        ))}
      </main>

      <FooterSection />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
